import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SignaturePad } from '../components/SignaturePad';
import { FileText, Loader2, ShieldCheck, ExternalLink, CheckCircle2, AlertTriangle, PenLine } from 'lucide-react';

// Client public : cookie httpOnly posé par /session, en-tête anti-CSRF sur les POST.
const signApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/public/sign`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
});

const fmtMoney = (v) => new Intl.NumberFormat('fr-DZ', { maximumFractionDigits: 2 }).format(v || 0) + ' DZD';
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }); } catch { return ''; } };

export default function SignPage() {
  const [phase, setPhase] = useState('loading'); // loading | ready | submitting | done | error
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [signature, setSignature] = useState(null);
  const [result, setResult] = useState(null);

  const [token] = useState(() => (window.location.hash || '').replace(/^#/, '').trim());
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Nettoie le fragment immédiatement : le token ne reste pas dans l'URL / l'historique
    window.history.replaceState(null, '', window.location.pathname);
    if (!token) { setError('Ce lien est incomplet. Demandez un nouveau lien de signature à votre fournisseur.'); setPhase('error'); return; }
    signApi.post('/session', { token })
      .then(({ data }) => { setSummary(data); setPhase('ready'); })
      .catch((err) => { setError(err.response?.data?.detail || 'Impossible d\'ouvrir ce lien de signature.'); setPhase('error'); });
  }, [token]);

  const openPdf = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/public/sign/pdf`, '_blank', 'noopener');

  const submit = async (e) => {
    e.preventDefault();
    if (!signature) { setError('Tracez votre signature avant de valider.'); return; }
    if (name.trim().length < 2) { setError('Indiquez votre nom complet.'); return; }
    setError(''); setPhase('submitting');
    try {
      const { data } = await signApi.post('', { signer_name: name.trim(), signature });
      setResult(data); setPhase('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'La signature n\'a pas pu être enregistrée.');
      setPhase(err.response?.status === 409 || err.response?.status === 410 ? 'error' : 'ready');
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col" data-testid="sign-page">
      <header className="bg-[#0A3D73] text-white px-5 py-4 shadow-md">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center"><PenLine className="w-5 h-5" /></div>
          <div>
            <p className="text-xs uppercase tracking-widest text-white/70">Signature électronique</p>
            <p className="font-semibold leading-tight">{summary?.company_name || 'R2A Industrie'}</p>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 py-6">
        <div className="max-w-xl mx-auto space-y-4">
          {phase === 'loading' && (
            <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-[#0A3D73]" />Ouverture sécurisée du document…</div>
          )}

          {phase === 'error' && (
            <div className="bg-white rounded-xl border border-red-200 p-6 text-center" data-testid="sign-error">
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <h1 className="text-lg font-semibold text-slate-900 mb-1">Lien indisponible</h1>
              <p className="text-sm text-slate-600">{error}</p>
            </div>
          )}

          {phase === 'done' && result && (
            <div className="bg-white rounded-xl border border-emerald-200 p-6 text-center animate-in fade-in zoom-in-95 duration-300" data-testid="sign-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
              <h1 className="text-lg font-semibold text-slate-900 mb-1">Document signé</h1>
              <p className="text-sm text-slate-600">Votre signature sur <span className="font-mono font-semibold text-[#0A3D73]">{result.doc_number}</span> a bien été enregistrée le {fmtDate(result.signed_at)}. {summary?.company_name} vous transmettra le document signé.</p>
              <p className="mt-4 text-[11px] text-slate-400 break-all">Empreinte SHA-256 : {result.pdf_sha256}</p>
            </div>
          )}

          {(phase === 'ready' || phase === 'submitting') && summary && (
            <>
              <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid="sign-summary">
                <p className="text-sm text-slate-600 leading-relaxed">
                  <span className="font-semibold text-slate-900">{summary.client_name}</span>, vous êtes invité à signer la {summary.doc_label.toLowerCase()} <span className="font-mono font-semibold text-[#0A3D73]">{summary.doc_number}</span> émise par <span className="font-semibold text-slate-900">{summary.company_name}</span>.
                </p>
                <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-slate-50 rounded-md p-3"><dt className="text-xs text-slate-500">Date</dt><dd className="font-medium text-slate-900">{fmtDate(summary.created_at)}</dd></div>
                  <div className="bg-slate-50 rounded-md p-3"><dt className="text-xs text-slate-500">Net à payer</dt><dd className="font-semibold text-[#0A3D73]" data-testid="sign-total">{fmtMoney(summary.total)}</dd></div>
                </dl>
                <Button type="button" variant="outline" onClick={openPdf} className="mt-4 w-full border-slate-300 h-11" data-testid="sign-view-pdf-btn">
                  <FileText className="w-4 h-4 mr-2" /> Lire le document complet <ExternalLink className="w-3.5 h-3.5 ml-2 text-slate-400" />
                </Button>
              </section>

              <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="sign-form">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">Votre signature</Label>
                  <SignaturePad onChange={setSignature} disabled={phase === 'submitting'} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="signer-name" className="text-sm font-medium text-slate-700">Nom et prénom du signataire</Label>
                  <Input id="signer-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Karim Benali" className="h-11 border-slate-300" autoComplete="name" data-testid="signer-name-input" />
                </div>
                {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="sign-form-error">{error}</p>}
                <Button type="submit" disabled={phase === 'submitting' || !signature || name.trim().length < 2} className="w-full h-12 text-base bg-[#0A3D73] hover:bg-[#082E56] text-white active:scale-[0.99] transition-transform" data-testid="sign-submit-btn">
                  {phase === 'submitting' ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <PenLine className="w-5 h-5 mr-2" />}Signer le document
                </Button>
                <p className="text-[11px] text-slate-500 flex items-start gap-1.5"><ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" /> En signant, vous approuvez ce document. Votre signature, la date, l'heure et votre adresse IP sont enregistrées ; le PDF final est scellé par une empreinte SHA-256.</p>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
